class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}
customElements.define('facet-short', FacetShort, { extends: 'select' });

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    this.minPriceInput.addEventListener('focus', this.minPriceInput.select);
    this.maxPriceInput.addEventListener('focus', this.maxPriceInput.select);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.maxPriceInput.value || event.target.max) - 1),
      event.target.min
    );
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.minPriceInput.value || event.target.min) + 1),
      event.target.max
    );
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value),
      parseInt(this.maxPriceInput.value || event.target.max) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value),
      parseInt(this.minPriceInput.value || event.target.min) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    FacetForm.cachedMap ||= new Map();
    FacetForm.renderedURL ||= window.location.href;
    this.cachedMap = FacetForm.cachedMap;

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange() {
    this.dirty = true;
    this.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  beforeRenderSection(request) {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);

    container.setAttribute('aria-busy', 'true');
    request.scrollTimer = setTimeout(() => {
      if (!this.isCurrentRequest(request)) return;
      const target = document.querySelector('.collection');
      if (target) {
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.scrollY - 200,
          behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth'
        });
      }
      if (loadings) {
        loadings.forEach((loading) => {
          loading.classList.add('btn--loading');
        });
      }
    }, 100);
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;
    container.setAttribute('aria-busy', 'false');

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) }
      ]
    ]);

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

  }

  renderSection(url, event) {
    const previous = FacetForm.activeRequest;
    if (previous) {
      clearTimeout(previous.scrollTimer);
      clearTimeout(previous.renderTimer);
      clearTimeout(previous.timeoutTimer);
      previous.controller.abort();
      this.settleRequest(previous, 'reject', new DOMException('Superseded', 'AbortError'));
    }
    const request = {
      url,
      event,
      controller: new AbortController(),
      update: this.startFilterUpdate(url),
      settled: false
    };
    FacetForm.activeRequest = request;
    document.getElementById('FacetRequestError')?.remove();
    this.beforeRenderSection(request);

    this.cachedMap.has(url)
      ? this.renderSectionFromCache(url, event, request)
      : this.renderSectionFromFetch(url, event, request);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  isCurrentRequest(request) {
    return FacetForm.activeRequest === request;
  }

  settleRequest(request, outcome, value) {
    if (request.settled) return;
    request.settled = true;
    clearTimeout(request.timeoutTimer);
    try {
      request.update?.[outcome](value);
    } catch (error) {
      console.error(error);
    }
  }

  restoreControls() {
    document.querySelectorAll('form[is="facet-form"]').forEach((form) => form.reset());
    document.querySelectorAll('select[is="facet-short"]').forEach((select) => {
      const selected = Array.from(select.options).find((option) => option.defaultSelected);
      if (selected) select.value = selected.value;
      select.calcSelectWidth();
    });
    document.querySelectorAll('price-range').forEach((range) => {
      const inputs = range.querySelectorAll('input[type="range"]');
      if (inputs.length !== 2) return;
      inputs[0].parentElement.style.setProperty('--range-min', `${Number(inputs[0].value) / Number(inputs[0].max) * 100}%`);
      inputs[1].parentElement.style.setProperty('--range-max', `${Number(inputs[1].value) / Number(inputs[1].max) * 100}%`);
    });
  }

  recoverFocus(request) {
    const active = document.activeElement;
    const current = active !== document.body && active?.isConnected ? active : null;
    const remembered = request.focusElement;
    const target = current || (remembered?.isConnected ? remembered : remembered?.id && document.getElementById(remembered.id));
    const drawer = document.getElementById('FacetDrawer');
    if (drawer?.hasAttribute('open')) {
      if (current && !drawer.contains(current)) return;
      const next = target && drawer.contains(target) ? target : drawer.focusElement;
      FoxTheme.a11y.trapFocus(drawer, next);
      FoxTheme.a11y.trapFocusHandlers.focusin({ target: next });
    } else if (target && !current) {
      target.focus({ preventScroll: true });
    }
  }

  failRequest(request, error) {
    if (!this.isCurrentRequest(request)) return;
    request.focusElement ||= document.activeElement;
    clearTimeout(request.scrollTimer);
    clearTimeout(request.renderTimer);
    this.settleRequest(request, 'reject', error);
    if (this.hasAttribute('data-history')) this.updateURLHash(FacetForm.renderedURL);
    this.restoreControls();
    this.afterRenderSection();
    try {
      this.recoverFocus(request);
    } catch (focusError) {
      console.error(focusError);
    }
    const container = document.getElementById('ProductGridContainer');
    const notice = document.createElement('p');
    notice.id = 'FacetRequestError';
    notice.className = 'alert alert--error';
    notice.setAttribute('role', 'alert');
    notice.append(`${this.dataset.filterLabel}: ${this.dataset.errorLabel}. `);
    const retry = document.createElement('a');
    const retryURL = new URL(request.url);
    retryURL.searchParams.delete('section_id');
    retry.href = retryURL.toString();
    retry.className = 'reversed-link';
    retry.style.textDecoration = 'underline';
    retry.style.textUnderlineOffset = '0.15em';
    retry.textContent = this.dataset.refreshLabel;
    notice.append(retry);
    container.prepend(notice);
  }

  /**
   * Dispatches shopify:collection:update or shopify:search:update as the filtered results are requested.
   * Resolves with the number of results once they render.
   */
  startFilterUpdate(url) {
    const library = FoxTheme.storefrontEvents?.library;
    if (!library) return null;

    const params = new URL(url).searchParams;
    const parseOptions = { locale: window.Shopify?.locale };
    const { pageType, collectionId, collectionHandle, productsCount } = this.dataset;

    let filterUpdate = null;
    if (pageType === 'search') {
      filterUpdate = FoxTheme.storefrontEvents.start(this, 'SearchUpdateEvent', {
        search: {
          query: params.get('q') ?? new URLSearchParams(window.location.search).get('q') ?? '',
          productFilters: library.SearchUpdateEvent.parseProductFilters(params, parseOptions),
          sortKey: library.SearchUpdateEvent.getSortKey(params)
        }
      });
    } else if (pageType === 'collection') {
      filterUpdate = FoxTheme.storefrontEvents.start(this, 'CollectionUpdateEvent', {
        // Built-in collections such as /collections/all have no id.
        collection: { id: collectionId || null, handle: collectionHandle, productsCount: Number(productsCount) },
        productFilters: library.CollectionUpdateEvent.parseProductFilters(params, parseOptions),
        sortKey: library.CollectionUpdateEvent.getSortKey(params)
      });
    }
    if (!filterUpdate) return null;

    return {
      resolve: (responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const count = Number(html.getElementById('ProductGridContainer')?.dataset.productsCount) || 0;
        filterUpdate.resolve(pageType === 'search' ? { totalCount: count } : { productsCount: count });
      },
      reject: (error) => filterUpdate.reject(error)
    };
  }

  renderSectionFromFetch(url, event, request) {
    const start = performance.now();
    request.timeoutTimer = setTimeout(() => {
      if (!this.isCurrentRequest(request) || request.settled) return;
      this.failRequest(request, new Error('Filter request timed out'));
      request.controller.abort();
    }, 15000);
    fetch(url, { signal: request.controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (!this.isCurrentRequest(request) || request.settled) return;
        const execution = performance.now() - start;
        request.renderTimer = setTimeout(
          () => this.applySection(responseText, event, request),
          execution > 250 ? 0 : 250
        );
      })
      .catch((error) => {
        if (!request.settled) this.failRequest(request, error);
      });
  }

  renderSectionFromCache(url, event, request) {
    request.renderTimer = setTimeout(() => this.applySection(this.cachedMap.get(url), event, request), 250);
  }

  applySection(responseText, event, request) {
    if (!this.isCurrentRequest(request) || request.settled) return;
    request.focusElement = document.activeElement;
    const ids = ['FacetFiltersContainer', 'DrawerFacetFiltersContainer', 'ProductGridContainer', 'FacetFiltersActive', 'ProductCount', 'SortByContainer', 'SortByContainerMobile'];
    const before = ids.map((id) => {
      const element = document.getElementById(id);
      return { element, html: element?.innerHTML, count: element?.dataset.productsCount };
    });
    try {
      const parsed = new DOMParser().parseFromString(responseText, 'text/html');
      if (!parsed.getElementById('ProductGridContainer')) throw new Error('Missing product grid');
      for (const { element } of before) {
        if (element && !parsed.getElementById(element.id)) throw new Error('Incomplete filter section');
      }
      this.renderFilters(responseText, event);
      this.renderFiltersActive(responseText);
      this.renderProductGridContainer(responseText);
      this.renderFacetFiltersContainer(responseText);
      this.renderProductCount(responseText);
      this.renderSortBy(responseText);
      this.renderSortByMobile(responseText);
      this.afterRenderSection();
    } catch (error) {
      before.forEach(({ element, html, count }) => {
        if (!element) return;
        element.innerHTML = html;
        if (count !== undefined) element.dataset.productsCount = count;
      });
      this.failRequest(request, error);
      return;
    }
    const renderedURL = new URL(request.url);
    renderedURL.searchParams.delete('section_id');
    FacetForm.renderedURL = renderedURL.toString();
    this.cachedMap.set(request.url, responseText);
    if (this.cachedMap.size > 32) this.cachedMap.delete(this.cachedMap.keys().next().value);
    this.settleRequest(request, 'resolve', responseText);
    try {
      this.recoverFocus(request);
      document.dispatchEvent(new CustomEvent('collection:rerendered'));
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText });
    } catch (error) {
      console.error(error);
    }
  }

  renderFilters(responseText, event) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const facetElements = parsedHTML.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter]'
    );

    const facetElementsFormDom = document.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter]'
    );

    Array.from(facetElementsFormDom).forEach((currentElement) => {
      if (!Array.from(facetElements).some(({ id }) => currentElement.id === id)) {
        currentElement.classList.add('hidden');
      }
    });

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    facetsToRender.forEach((element) => {
      const filter = document.querySelector(`[data-filter][data-index="${element.dataset.index}"]`);
      if (filter !== null) {
        filter.classList.remove('hidden');
        if (filter.tagName === 'DETAILS') {
          filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
        } else {
          filter.innerHTML = element.innerHTML;
        }
      }
    });
  }

  renderFiltersActive(responseText) {
    const id = 'FacetFiltersActive';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductGridContainer(responseText) {
    const id = 'ProductGridContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    const source = parsedHTML.getElementById(id);
    document.getElementById(id).innerHTML = source && source.innerHTML;
    if (source?.hasAttribute('data-products-count')) {
      document.getElementById(id).dataset.productsCount = source.dataset.productsCount;
    }

    const layoutSwitcher = document.querySelector('layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
  }

  renderFacetFiltersContainer(responseText) {
    const id = 'FacetFiltersContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductCount(responseText) {
    const id = 'ProductCount';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortBy(responseText) {
    const id = 'SortByContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortByMobile(responseText) {
    const id = 'SortByContainerMobile';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}
customElements.define('facet-remove', FacetRemove, { extends: 'a' });

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    this.innerText = facetCount && facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class FacetToggler extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.facetEl = document.getElementById('FacetFiltersContainer');
    this.addEventListener('click', this.onClickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick(evt) {
    evt.preventDefault();

    if (this.facetEl.classList.contains('xl:block')) {
      this.facetEl.classList.remove('xl:block');
    } else {
      this.facetEl.classList.add('xl:block');
    }
  }
}
customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') == 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return;
    this.loadingState();

    const url = this.setUrl().toString();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderPagination(responseText);
        this.renderProductGridContainer(responseText);
      });
  }

  renderPagination(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const pagination = productGridContainer.querySelector('.pagination');
    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productList = productGridContainer.querySelector('.products-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.f-column').forEach((item) => {
        productList.appendChild(item);
      });
    }
  }

  setUrl() {
    const url = new URL(this.getAttribute('action'));
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }
}
customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'hypertheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.classList.contains('btn--active')));
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  /**
   * @param {event|element} evt
   */
  onButtonClick(evt) {
    const target = evt.currentTarget ? evt.currentTarget : evt;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);
        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;

    productGrid.dataset.layout = layoutMode;

    const { gridClass, listClass } = productGrid.dataset;
    if ((gridClass || listClass) && gridClass !== listClass) {
      const productCards = productGrid.querySelectorAll('.product-card');
      productCards &&
        productCards.forEach((card) => {
          if ('grid' === layoutMode) {
            listClass && card.classList.remove(listClass);
            gridClass && card.classList.add(gridClass);
          } else {
            gridClass && card.classList.remove(gridClass);
            listClass && card.classList.add(listClass);
          }
        });
    }

    this.buttons.forEach((button) => {
      button.classList.toggle('btn--active', button === target);
      button.setAttribute('aria-pressed', String(button === target));
    });

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
customElements.define('layout-switcher', LayoutSwitcher);
